import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiFineService } from '../../../../../core/services/api/fine/api-fine.service';
import { Fine } from '../../../../../core/models/fine/fine.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalFineComponent } from "../../../modals/finance/fine/modal-fine/modal-fine.component";

@Component({
  selector: 'app-fine',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalFineComponent],
  templateUrl: './fine.component.html',
  styleUrl: './fine.component.css'
})
export class FineComponent implements OnInit {
  public fines: Fine[] = [];
  public filteredFines: Fine[] = [];
  public isLoading: boolean = false;

  // Management State
  public isModalOpen: boolean = false;
  public selectedFine: Fine | null = null;

  // Pagination State
  public currentPage: number = 1;
  public pageSize: number = 10;
  public totalItems: number = 0;
  public pageSizeOptions: number[] = [10, 20, 50, 100];

  // Search & Filter State
  public searchText: string = '';
  public isFilterDropdownOpen: boolean = false;
  public isVatDropdownOpen: boolean = false;
  public filterSite: string = '';
  public filterVat: string = ''; // '' | 'yes' | 'no'
  public filterMaxAmount: number | null = null;
  public siteOptions: string[] = [];

  // Sorting State
  public sortColumn: string = '';
  public sortDirection: 'asc' | 'desc' = 'asc';

  // Dashboard Stats
  public stats = [
    { title: 'ประเภทค่าปรับทั้งหมด', value: 0, icon: 'receipt_long', color: 'text-indigo-600 bg-indigo-50' },
    { title: 'รายได้ค่าปรับเฉลี่ย', value: 0, icon: 'trending_up', color: 'text-emerald-600 bg-emerald-50' },
    { title: 'โครงการที่ใช้งาน', value: 0, icon: 'place', color: 'text-amber-600 bg-amber-50' },
    { title: 'ค่าปรับสูงสุด', value: 0, icon: 'price_check', color: 'text-rose-600 bg-rose-50' }
  ];

  constructor(private apiFineService: ApiFineService) { }

  ngOnInit(): void {
    this.getFines();
  }

  public getFines(): void {
    this.isLoading = true;
    this.apiFineService.getFines().subscribe({
      next: (res) => {
        this.fines = res || [];
        if (this.fines.length === 0) this.loadMockData();
        this.siteOptions = Array.from(new Set(this.fines.map(f => f.site))).filter(s => s).sort();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('API error:', err);
        this.loadMockData();
        this.siteOptions = Array.from(new Set(this.fines.map(f => f.site))).filter(s => s).sort();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      }
    });
  }

  private loadMockData(): void {
    this.fines = [
      { id: 1, name: 'ค่าปรับบัตรหาย', amount: 300, isVatIncluded: true, revenueName: 'ค่าปรับบัตรจอดรถหาย', site: 'The Market' },
      { id: 2, name: 'ค่าปรับฝ่าฝืนจอดรถ', amount: 500, isVatIncluded: true, revenueName: 'ค่าปรับผิดระเบียบ', site: 'The Market' },
      { id: 3, name: 'ค่าปรับคืนบัตรช้า', amount: 100, isVatIncluded: false, revenueName: 'ค่าปรับรายชั่วโมงเกินกำหนด', site: 'AroundD Lifestyle' }
    ];
  }

  private calculateStats(): void {
    this.stats[0].value = this.fines.length;
    this.stats[1].value = this.fines.reduce((acc, curr) => acc + curr.amount, 0) / (this.fines.length || 1);
    this.stats[2].value = this.siteOptions.length;
    this.stats[3].value = Math.max(...this.fines.map(f => f.amount), 0);
  }

  // --- Filtering & Sorting ---
  public applyFilters(): void {
    let result = [...this.fines];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      result = result.filter(f =>
        f.name.toLowerCase().includes(search) ||
        f.revenueName.toLowerCase().includes(search) ||
        f.site.toLowerCase().includes(search)
      );
    }

    if (this.filterSite) {
      result = result.filter(f => f.site === this.filterSite);
    }

    if (this.filterVat) {
      const isVat = this.filterVat === 'yes';
      result = result.filter(f => f.isVatIncluded === isVat);
    }

    if (this.filterMaxAmount !== null) {
      result = result.filter(f => f.amount <= (this.filterMaxAmount ?? Infinity));
    }

    if (this.sortColumn) {
      result.sort((a: any, b: any) => {
        const valA = a[this.sortColumn];
        const valB = b[this.sortColumn];
        if (typeof valA === 'string') {
          return this.sortDirection === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        return this.sortDirection === 'asc' ? valA - valB : valB - valA;
      });
    }

    this.totalItems = result.length;
    this.filteredFines = result;
  }

  public sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  public toggleFilterDropdown(): void {
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
  }

  public selectSiteFilter(site: string): void {
    this.filterSite = site;
    this.isFilterDropdownOpen = false;
    this.applyFilters();
    this.currentPage = 1;
  }

  public selectVatFilter(value: string): void {
    this.filterVat = value;
    this.isVatDropdownOpen = false;
    this.applyFilters();
    this.currentPage = 1;
  }

  public onAmountFilterChange(): void {
    this.applyFilters();
    this.currentPage = 1;
  }

  // --- Pagination ---
  public get paginatedFines(): Fine[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredFines.slice(start, start + this.pageSize);
  }

  public get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize) || 1;
  }

  public get pagesArray(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  public onPageSizeChange(event: any): void {
    this.pageSize = +event.target.value;
    this.currentPage = 1;
    this.applyFilters();
  }

  // --- Actions ---
  public onRefresh(): void {
    this.getFines();
  }

  public openCreateModal(): void {
    this.selectedFine = null;
    this.isModalOpen = true;
  }

  public openEditModal(fine: Fine): void {
    this.selectedFine = { ...fine };
    this.isModalOpen = true;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedFine = null;
  }
}
